import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injectable,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  CalendarEvent,
  CalendarEventTimesChangedEvent,
  CalendarEventTitleFormatter,
  CalendarModule,
} from 'angular-calendar';
import { WeekViewHourSegment } from 'calendar-utils';
import { Subscription, fromEvent } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { addDays, addMinutes, endOfWeek } from 'date-fns';
import { AuthenticationService } from '../../../_services/authentication/authentication.service';
import { CustomersService } from '../../../_services/customers/customers.service';
import IUser from '../../../_interfaces/IUser';
import ISite from '../../../_interfaces/ISite';
import IShift from '../../../_interfaces/IShift';
import moment from 'moment';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { ShiftsService } from '../../../_services/shifts/shifts.service';
import { SessionService } from '../../../_services/session/session.service';
import { BaseComponent } from '../../../components/base/base.component';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { CommonModule } from '@angular/common';

function floorToNearest(amount: number, precision: number) {
  return Math.floor(amount / precision) * precision;
}

function ceilToNearest(amount: number, precision: number) {
  return Math.ceil(amount / precision) * precision;
}

@Injectable()
export class CustomEventTitleFormatter extends CalendarEventTitleFormatter {
  weekTooltip(event: CalendarEvent, title: string) {
    if (!event.meta.tmpEvent) {
      return super.weekTooltip(event, title);
    }
    return undefined;
  }

  dayTooltip(event: CalendarEvent, title: string) {
    if (!event.meta.tmpEvent) {
      return super.dayTooltip(event, title);
    }
    return undefined;
  }
}

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-admin-schedules',
  templateUrl: './admin-schedules.component.html',
  styleUrls: ['./admin-schedules.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    TranslatePipe,

    SelectModule,
    FormsModule,
    MultiSelectModule,
    CalendarModule,
  ],
  providers: [
    {
      provide: CalendarEventTitleFormatter,
      useClass: CustomEventTitleFormatter,
    },
  ],
  styles: [
    `
      .cal-header > span {
        visibility: hidden;
      }

      .cal-week-view .cal-time-events .cal-day-column {
        margin-right: 10px;
      }

      .cal-week-view .cal-hour {
        width: calc(100% + 10px);
      }
      .disable-hover {
        pointer-events: none;
      }
    `,
  ],
})
export class AdminSchedulesComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  viewDate = new Date();
  events: CalendarEvent[] = [];
  dragToCreateActive = false;
  sites: ISite[];
  users: IUser[];
  visibleSites: ISite[] = [];
  selectedSite: ISite;
  selectedUser: IUser;
  locale: string;
  weekStartsOn: 0 = 0;
  subscriptionTranslate: Subscription;
  loading = true;
  loadError = false;

  constructor(
    public cdr: ChangeDetectorRef,
    public authService: AuthenticationService,
    public session: SessionService,
    public confirmationService: ConfirmationService,
    public customersService: CustomersService,
    public errorsService: ErrorsService,
    public messageService: MessageService,
    public shiftsService: ShiftsService,
    public translate: TranslateService
  ) {
    super(session);
  }

  public loadUsers() {
    this.customersService
      .getUsers(this.customers_id)
      .subscribe({
        next: (users: IUser[]) => {
          this.users = users;
          this.refresh();
        },
        error: () => {
          this.loadError = true;
          this.loading = false;
          this.refresh();
        },
      });
  }

  public loadShifts() {
    this.loading = true;
    this.loadError = false;

    this.shiftsService
      .get(this.customers_id)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.refresh();
        }),
      )
      .subscribe({
        next: (shifts: IShift[]) => {
          const ids = this.visibleSites.map((site) => site.id);
          this.events = shifts
            .filter((shift) => {
              return ids.includes(shift.sites_id);
            })
            .map(this.mapShiftToEvent.bind(this));
        },
        error: () => {
          this.loadError = true;
        },
      });
  }

  public loadSites() {
    this.customersService
      .getSites(this.customers_id)
      .subscribe({
        next: (sites: ISite[]) => {
          this.visibleSites = sites;
          this.sites = sites;
          this.loadShifts();
        },
        error: () => {
          this.loadError = true;
          this.loading = false;
          this.refresh();
        },
      });
  }

  public getShiftDescription(site: ISite, user: IUser) {
    const fullname = `${user.name}${user.surname ? ' ' + user.surname : ''}`;
    return fullname + '<br>' + site.name;
  }

  public mapShiftToEvent(shift: IShift) {
    const shiftDescription = this.getShiftDescription(shift.site, shift.user);
    return {
      id: shift.id,
      shift: shift,
      color: {
        primary: shift.site.color,
        secondary: shift.site.color,
      },
      title: shiftDescription,
      start: this.calculateShiftStartDate(shift),
      end: this.calculateShiftEndDate(shift),
      meta: {
        tmpEvent: false,
      },
      resizable: {
        beforeStart: true,
        afterEnd: true,
      },
      cssClass: 'Shift__Event',
    };
  }

  public calculateShiftStartDate(shift: IShift) {
    const startOfWeek = moment().startOf('week');
    const shiftStartDate = startOfWeek
      .add(shift.weekday, 'days')
      .hour(shift.hour_from)
      .minute(shift.minute_from);
    return shiftStartDate.toDate();
  }

  public calculateShiftEndDate(shift: IShift) {
    const startOfWeek = moment().startOf('week');
    const shiftEndDate = startOfWeek
      .add(shift.weekday, 'days')
      .hour(shift.hour_to)
      .minute(shift.minute_to);
    return shiftEndDate.toDate();
  }

  public eventTimesChanged({
    event,
    newStart,
    newEnd,
  }: CalendarEventTimesChangedEvent): void {
    const shift: IShift = (event as any).shift;
    event.start = newStart;
    event.end = newEnd;
    this.refresh();
    this.shiftsService
      .delete(this.customers_id, shift.sites_id, shift.users_id, shift.id)
      .subscribe(() => {
        this.saveShift(event, shift.site, shift.user);
      });
  }

  public startDragToCreate(
    segment: WeekViewHourSegment,
    mouseDownEvent: MouseEvent,
    segmentElement: HTMLElement
  ) {
    if (!this.validateSelection()) {
      return;
    }

    const dragToSelectEvent = this.createDragEvent(segment);
    this.initializeDrag(segmentElement, dragToSelectEvent);

    fromEvent(document, 'mousemove')
      .pipe(
        finalize(() => this.finalizeDrag(dragToSelectEvent)),
        takeUntil(fromEvent(document, 'mouseup'))
      )
      .subscribe((mouseMoveEvent: MouseEvent) =>
        this.handleMouseMove(
          mouseMoveEvent,
          segment,
          segmentElement,
          dragToSelectEvent
        )
      );
  }

  public validateSelection(): boolean {
    if (!!!this.selectedUser || !!!this.selectedSite) {
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant(
          'MESSAGES.SCHEDULES.SELECTION.ERROR.SUMMARY'
        ),
        detail: this.translate.instant(
          'MESSAGES.SCHEDULES.SELECTION.ERROR.DETAIL'
        ),
      });
      return false;
    }
    return true;
  }

  private createDragEvent(segment: WeekViewHourSegment): CalendarEvent {
    const description = this.getShiftDescription(
      this.selectedSite,
      this.selectedUser
    );
    return {
      id: this.events.length,
      color: {
        primary: this.selectedSite.color,
        secondary: this.selectedSite.color,
      },
      title: description,
      start: segment.date,
      meta: { tmpEvent: true },
      cssClass: 'Shift__Event',
    };
  }

  private initializeDrag(segmentElement: HTMLElement, dragToSelectEvent): void {
    this.events = [...this.events, dragToSelectEvent];
    this.dragToCreateActive = true;
  }

  private handleMouseMove(
    mouseMoveEvent: MouseEvent,
    segment: WeekViewHourSegment,
    segmentElement: HTMLElement,
    dragToSelectEvent: CalendarEvent
  ): void {
    const segmentPosition = segmentElement.getBoundingClientRect();
    const minutesDiff = ceilToNearest(
      mouseMoveEvent.clientY - segmentPosition.top,
      30
    );
    const daysDiff =
      floorToNearest(
        mouseMoveEvent.clientX - segmentPosition.left,
        segmentPosition.width
      ) / segmentPosition.width;
    const newEnd = addDays(addMinutes(segment.date, minutesDiff), daysDiff);
    if (
      newEnd > segment.date &&
      newEnd < endOfWeek(this.viewDate, { weekStartsOn: this.weekStartsOn })
    ) {
      dragToSelectEvent.end = newEnd;
    }
    this.refresh();
  }

  private finalizeDrag(dragToSelectEvent: CalendarEvent): void {
    delete dragToSelectEvent.meta.tmpEvent;
    this.dragToCreateActive = false;
    this.refresh();
    this.saveShift(dragToSelectEvent, this.selectedSite, this.selectedUser);
  }

  public saveShift(event: CalendarEvent, site: ISite, user: IUser) {
    const moment_start = moment(event.start);
    const moment_end = moment(event.end);

    if (moment_start.weekday() != moment_end.weekday()) {
      this.messageService.add({
        life: 5000,
        severity: 'error',
        summary: this.translate.instant(
          'MESSAGES.SCHEDULES.DAYS.ERROR.SUMMARY'
        ),
        detail: this.translate.instant('MESSAGES.SCHEDULES.DAYS.ERROR.DETAIL'),
      });
      return this.loadShifts();
    }

    const shift: IShift = {
      weekday: moment_start.weekday(),
      hour_from: +moment_start.format('HH'),
      hour_to: +moment_end.format('HH'),
      minute_from: +moment_start.format('mm'),
      minute_to: +moment_end.format('mm'),
    };
    this.shiftsService
      .create(this.customers_id, site.id, user.id, shift)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant(
              'MESSAGES.SCHEDULES.CREATE.SUMMARY'
            ),
            detail: this.translate.instant('MESSAGES.SCHEDULES.CREATE.DETAIL', {
              name: user.name,
              site: site.name,
              weekday: moment_start.format('dddd'),
              start: moment_start.format('HH:mm'),
              end: moment_end.format('HH:mm'),
            }),
          });
          this.loadShifts();
        },
        error: (error) => {
          const code = this.errorsService.getMessageFromShiftError(error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('MESSAGES.SCHEDULES.ERROR.SUMMARY'),
            detail: this.translate.instant(`MESSAGES.SCHEDULES.ERROR.${code}`),
          });
        },
      });
  }

  public openEvent(event: CalendarEvent) {
    const shift = event['shift'];
    this.confirmationService.confirm({
      header: this.translate.instant('ADMIN.SCHEDULES.CONFIRM.DELETE.TITLE'),
      message: this.translate.instant('ADMIN.SCHEDULES.CONFIRM.DELETE.TEXT'),
      accept: this.deleteShift.bind(this, shift, true),
    });
  }

  public deleteShift(shift: IShift, displayMessage: boolean = false) {
    const reference_start = moment().startOf('week').add(shift.weekday, 'days');
    reference_start.hour(shift.hour_from);
    reference_start.minutes(shift.minute_from);

    const reference_end = moment().startOf('week').add(shift.weekday, 'days');
    reference_end.hour(shift.hour_to);
    reference_end.minutes(shift.minute_to);

    this.shiftsService
      .delete(this.customers_id, shift.sites_id, shift.users_id, shift.id)
      .subscribe({
        next: () => {
          if (displayMessage) {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant(
                'MESSAGES.SCHEDULES.DELETE.SUMMARY'
              ),
              detail: this.translate.instant(
                'MESSAGES.SCHEDULES.DELETE.DETAIL',
                {
                  name: shift.user.name,
                  site: shift.site.name,
                  weekday: reference_start.format('dddd'),
                  start: reference_start.format('HH:mm'),
                  end: reference_end.format('HH:mm'),
                }
              ),
            });
          }
          this.loadShifts();
        },
        error: (error) => {
          this.errorsService.getMessageFromShiftError(error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('MESSAGES.SCHEDULES.ERROR.SUMMARY'),
            detail: this.translate.instant('MESSAGES.SCHEDULES.ERROR.DETAIL'),
          });
        },
      });
  }

  private refresh() {
    this.events = [...this.events];
    this.cdr.detectChanges();
  }

  get hasNoSites(): boolean {
    return !this.loading && !this.loadError && (!this.sites || this.sites.length === 0);
  }

  public retryLoad(): void {
    this.loadUsers();
    this.loadSites();
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.locale = this.translate.currentLang;

    this.loadUsers();
    this.loadSites();

    this.subscriptionTranslate = this.translate.onLangChange.subscribe(() => {
      this.locale = this.translate.currentLang;
    });
  }

  public ngOnDestroy(): void {
    if (this.subscriptionTranslate) {
      this.subscriptionTranslate.unsubscribe();
    }
  }
}
